export interface IAdjuntoItemData {
  nombre?: string;
  descripcion?: string;
  properties?: Record<string, unknown>;
}

export interface IAdjuntoItem {
  id?: number;
  archivo?: {
    id?: number;
    nombre?: string;
    path?: string;
    contenidoUuid?: string;
    esDirectorio?: boolean;
  };
  data?: IAdjuntoItemData;
}

export interface IActividadAdjunto {
  id?: number;
  cantidad?: number;
  items?: IAdjuntoItem[];
}

export interface SubirAdjuntoPayload {
  file: File;
  nombre?: string;
  descripcion?: string;
  actionName?: string;
  /** Clave del part binario; default `file1` según UsuarioMeActividad.MD */
  partKey?: string;
}

/** Clave estándar del part binario en multipart (doc: `file1`) */
export const ACTIVIDAD_ADJUNTO_PART_KEY = 'file1';

const ADJUNTO_ERROR_MESSAGES: Record<string, string> = {
  filenamemissing: 'Falta el nombre del archivo (archivoNombre).',
  filemissing: 'No se recibió el archivo en el multipart.',
  filereaderror: 'No se pudo leer el archivo enviado.',
};

function authHeader(): Record<string, string> {
  const token = localStorage.getItem('jhi-authenticationToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function resolveAdjuntoDisplayNombre(item: IAdjuntoItem): string {
  const nombre = item.data?.nombre?.trim() || item.archivo?.nombre?.trim();
  return nombre || '—';
}

export function resolveAdjuntoDescripcion(item: IAdjuntoItem): string {
  return item.data?.descripcion?.trim() || '';
}

export function parseActividadApiError(status: number, responseText: string): string {
  if (!responseText) return `Error ${status}`;
  try {
    const body = JSON.parse(responseText) as Record<string, unknown>;
    const errorKey = String(
      body.errorKey ?? (body.params as Record<string, unknown> | undefined)?.errorKey ?? '',
    ).toLowerCase();
    if (errorKey && ADJUNTO_ERROR_MESSAGES[errorKey]) {
      return ADJUNTO_ERROR_MESSAGES[errorKey];
    }
    return String(
      body.details ?? body.detail ?? body.message ?? body.title ?? body.error ?? responseText,
    );
  } catch {
    return responseText;
  }
}

/** Agrega campos multipart de un adjunto (UsuarioMeActividad.MD) */
export function appendAdjuntoMultipartFields(
  form: FormData,
  file: File,
  options?: { nombre?: string; descripcion?: string; partKey?: string },
): string {
  const partKey = options?.partKey?.trim() || ACTIVIDAD_ADJUNTO_PART_KEY;
  const nombre = options?.nombre?.trim() || file.name;
  if (!nombre) {
    throw new Error('El nombre del archivo es obligatorio.');
  }
  form.append('archivoId', partKey);
  form.append('archivoNombre', nombre);
  if (options?.descripcion?.trim()) {
    form.append('descripcion', options.descripcion.trim());
  }
  form.append(partKey, file, file.name);
  return partKey;
}

/** Valores del formulario dinámico en el mismo POST de acción */
export function appendActividadFormValues(form: FormData, values: Record<string, unknown>): void {
  form.append('data', JSON.stringify(values));
}

export async function ejecutarActividadFormAction(
  actividadId: string | number,
  actionName: string,
  form: FormData,
): Promise<Record<string, unknown> | null> {
  const res = await fetch(
    `/api/usuario/me/actividad/${actividadId}/form/action/${encodeURIComponent(actionName)}`,
    {
      method: 'POST',
      headers: { ...authHeader() },
      body: form,
    },
  );
  const responseText = await res.text();
  if (!res.ok) {
    throw new Error(parseActividadApiError(res.status, responseText));
  }
  if (!responseText) return null;
  try {
    return JSON.parse(responseText) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export async function listarActividadAdjuntos(
  actividadId: string | number,
): Promise<IActividadAdjunto | null> {
  const res = await fetch(`/api/usuario/me/actividad/${actividadId}/adjuntos`, {
    headers: { ...authHeader() },
  });
  if (res.status === 404) return null;
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(parseActividadApiError(res.status, text));
  }
  return res.json() as Promise<IActividadAdjunto>;
}

/** Adjuntos de un nodo instancia vía detalle (`GET .../profase-workflow-nodo-instancias/{id}`). */
export async function listarNodoInstanciaAdjuntos(
  nodoInstanciaId: string | number,
): Promise<IAdjuntoItem[]> {
  const res = await fetch(`/api/profase-workflow-nodo-instancias/${nodoInstanciaId}`, {
    headers: { ...authHeader() },
  });
  if (res.status === 404) return [];
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(parseActividadApiError(res.status, text));
  }
  const data = await res.json() as { adjunto?: IActividadAdjunto | null };
  return data?.adjunto?.items ?? [];
}

/** Sube un adjunto (una petición = un archivo). */
export async function subirActividadAdjunto(
  actividadId: string | number,
  payload: SubirAdjuntoPayload,
): Promise<Record<string, unknown> | null> {
  const actionName = payload.actionName?.trim() || 'save';
  const form = new FormData();
  appendAdjuntoMultipartFields(form, payload.file, {
    nombre: payload.nombre,
    descripcion: payload.descripcion,
    partKey: payload.partKey,
  });
  return ejecutarActividadFormAction(actividadId, actionName, form);
}

/** Elimina un item del adjunto (`DELETE .../adjuntos/{itemId}`). */
export async function eliminarActividadAdjunto(
  actividadId: string | number,
  itemId: number,
): Promise<IActividadAdjunto> {
  const res = await fetch(
    `/api/usuario/me/actividad/${actividadId}/adjuntos/${itemId}`,
    {
      method: 'DELETE',
      headers: { ...authHeader() },
    },
  );
  const text = await res.text();
  if (!res.ok) {
    throw new Error(parseActividadApiError(res.status, text));
  }
  if (!text) return { items: [], cantidad: 0 };
  return JSON.parse(text) as IActividadAdjunto;
}

export async function fetchActividadAdjuntoBlob(
  actividadId: string | number,
  itemId: number,
): Promise<Blob> {
  const res = await fetch(
    `/api/usuario/me/actividad/${actividadId}/adjuntos/${itemId}/contenido`,
    { headers: { ...authHeader() } },
  );
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(parseActividadApiError(res.status, text));
  }
  return res.blob();
}

export async function descargarActividadAdjuntoContenido(
  actividadId: string | number,
  itemId: number,
  fallbackNombre: string,
): Promise<void> {
  const res = await fetch(
    `/api/usuario/me/actividad/${actividadId}/adjuntos/${itemId}/contenido`,
    { headers: { ...authHeader() } },
  );
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(parseActividadApiError(res.status, text));
  }

  const blob = await res.blob();
  const contentDisposition = res.headers.get('content-disposition');
  const filename = parseContentDispositionFilename(contentDisposition, fallbackNombre);
  const href = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = href;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(href);
  }
}

function parseContentDispositionFilename(contentDisposition: string | null, fallback: string): string {
  let filename = fallback.trim() || 'archivo';
  if (!contentDisposition) return filename;

  const star = /filename\*=UTF-8''([^;\s]+)/i.exec(contentDisposition);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].replace(/"/g, ''));
    } catch {
      return star[1];
    }
  }

  const quoted = /filename="([^"]+)"/i.exec(contentDisposition);
  const plain = quoted ?? /filename=([^;\s]+)/i.exec(contentDisposition);
  if (plain?.[1]) {
    return plain[1].replace(/"/g, '');
  }

  return filename;
}
